import { Controller } from "@hotwired/stimulus"
import { SurveyFlow } from "../modules/survey_flow"
import { SurveyAntiFraud } from "../modules/survey_anti_fraud"

export default class extends Controller {
  static targets = ["container", "loading", "content"]
  static values = {
    slug: String,
    surveyUrl: String,
    respondUrl: String,
    csrfToken: String,
    loggedIn: { type: Boolean, default: false }
  }

  connect() {
    this.antiFraud = new SurveyAntiFraud()
    this.loadSurvey()
  }

  async loadSurvey() {
    try {
      const url = this.surveyUrlValue || `/surveys/${this.slugValue}.json`
      const response = await fetch(url, {
        headers: { "Accept": "application/json" }
      })
      if (!response.ok) { this.showError("Survey not available"); return }

      this.survey = await response.json()
      this.loadFont()
      this.render()
    } catch {
      this.showError("Could not load survey")
    }
  }

  render() {
    this.loadingTarget.style.display = "none"
    if (!this.survey.open && !this.survey.show_results) {
      this.showClosed()
    } else if (this.survey.already_responded) {
      this.showAlreadyResponded()
    } else if (this.survey.open) {
      if (this.survey.skip_welcome) {
        setTimeout(() => this.startSurvey(), 400)
      } else {
        this.showWelcome()
      }
    } else {
      this.showClosed()
    }
  }

  showWelcome() {
    this.contentTarget.innerHTML = `
      <div class="survey-widget__welcome">
        <h4 class="survey-widget__title">${this.esc(this.survey.title)}</h4>
        ${this.survey.description ? `<p class="survey-widget__desc">${this.esc(this.survey.description)}</p>` : ""}
        <button class="survey-widget__btn survey-widget__btn--primary" data-action="click->survey-widget#startSurvey">
          Take Survey
        </button>
        ${this.survey.requires_login && !this.loggedInValue ? '<p class="survey-widget__note">Login required to participate</p>' : ""}
      </div>`
  }

  startSurvey() {
    if (this.survey.requires_login && !this.loggedInValue) {
      window.location.href = "/users/sign_in"
      return
    }

    this.antiFraud.recordStart()
    this.flow = new SurveyFlow(this.survey.questions, {
      container: this.contentTarget,
      onComplete: (answers) => this.submitResponses(answers),
      honeypotName: "_hp_email"
    })
    this.flow.start()
  }

  async submitResponses(answers) {
    this.flow.showSubmitting()

    const url = this.respondUrlValue || `/surveys/${this.slugValue}/respond.json`
    const body = {
      answers: answers,
      fingerprint: this.antiFraud.getFingerprint(),
      started_at: this.antiFraud.getStartedAt(),
      _hp_email: document.querySelector('[name="_hp_email"]')?.value || ""
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfTokenValue
        },
        body: JSON.stringify(body)
      })

      const data = await response.json()
      if (data.success) {
        this.showThankYou(data)
      } else {
        this.flow.showError(data.errors?.join(", ") || "Submission failed")
      }
    } catch {
      this.flow.showError("Network error. Please try again.")
    }
  }

  showThankYou(data) {
    const title = this.survey.thank_you_title || "Thank you!"
    const message = this.survey.thank_you_message || "Your response has been recorded."
    let html = `
      <div class="survey-widget__thankyou">
        <div class="survey-widget__checkmark">&#10003;</div>
        <h4 class="survey-widget__title">${this.esc(title)}</h4>
        <p class="survey-widget__desc">${this.esc(message)}</p>`

    if (data.show_results && data.results_url) {
      html += `<button class="survey-widget__btn survey-widget__btn--secondary"
                        data-action="click->survey-widget#loadResults"
                        data-results-url="${data.results_url}">
                 View Results
               </button>`
    }
    html += "</div>"
    this.contentTarget.innerHTML = html
  }

  showClosed() {
    this.contentTarget.innerHTML = `
      <div class="survey-widget__closed">
        <h4 class="survey-widget__title">${this.esc(this.survey.title)}</h4>
        <p class="survey-widget__desc">${this.esc(this.survey.closed_message || "This survey is now closed.")}</p>
        ${this.survey.show_results ? '<button class="survey-widget__btn survey-widget__btn--secondary" data-action="click->survey-widget#loadResults">View Results</button>' : ""}
      </div>`
  }

  showAlreadyResponded() {
    this.contentTarget.innerHTML = `
      <div class="survey-widget__responded">
        <h4 class="survey-widget__title">${this.esc(this.survey.title)}</h4>
        <p class="survey-widget__desc">You've already completed this survey.</p>
        ${this.survey.results_url ? '<button class="survey-widget__btn survey-widget__btn--secondary" data-action="click->survey-widget#loadResults">View Results</button>' : ""}
      </div>`
  }

  async loadResults(event) {
    const url = event?.currentTarget?.dataset?.resultsUrl ||
                this.survey.results_url ||
                `/surveys/${this.slugValue}/results.json`

    try {
      const response = await fetch(url, {
        headers: { "Accept": "application/json" }
      })
      const data = await response.json()
      if (data.available) {
        this.renderResults(data)
      }
    } catch {
      this.showError("Could not load results")
    }
  }

  renderResults(data) {
    let html = `<div class="survey-widget__results">
      <h4 class="survey-widget__title">Results</h4>
      <p class="survey-widget__desc">${data.response_count} responses</p>`

    data.questions.forEach(q => {
      html += `<div class="survey-widget__result-q">
        <p class="survey-widget__result-prompt">${this.esc(q.prompt)}</p>`

      if (q.options) {
        q.options.forEach(opt => {
          html += `<div class="survey-widget__result-bar">
            <div class="survey-widget__result-bar-label">
              <span>${this.esc(opt.value)}</span>
              <span>${opt.percentage}%</span>
            </div>
            <div class="survey-widget__result-bar-track">
              <div class="survey-widget__result-bar-fill" style="width:${opt.percentage}%"></div>
            </div>
          </div>`
        })
      } else if (q.average !== undefined) {
        html += `<div class="survey-widget__result-avg">Average: <strong>${q.average}</strong></div>`
      } else if (q.count !== undefined) {
        html += `<div class="survey-widget__result-avg">${q.count} text responses</div>`
      }

      html += "</div>"
    })

    html += "</div>"
    this.contentTarget.innerHTML = html
  }

  showError(msg) {
    if (this.hasLoadingTarget) this.loadingTarget.style.display = "none"
    this.contentTarget.innerHTML = `
      <div class="survey-widget__error">
        <p class="survey-widget__desc">${this.esc(msg)}</p>
      </div>`
  }

  loadFont() {
    const fontMap = {
      "inter": { family: "Inter", param: "Inter:wght@400;500;600;700" },
      "dm-sans": { family: "DM Sans", param: "DM+Sans:wght@400;500;600;700" },
      "outfit": { family: "Outfit", param: "Outfit:wght@400;500;600;700" },
      "plus-jakarta": { family: "Plus Jakarta Sans", param: "Plus+Jakarta+Sans:wght@400;500;600;700" },
      "nunito": { family: "Nunito", param: "Nunito:wght@400;600;700" },
      "merriweather": { family: "Merriweather", param: "Merriweather:wght@400;700" }
    }

    const key = this.survey.font_family
    if (!key || key === "default" || !fontMap[key]) return

    const font = fontMap[key]
    const link = document.createElement("link")
    link.rel = "stylesheet"
    link.href = `https://fonts.googleapis.com/css2?family=${font.param}&display=swap`
    document.head.appendChild(link)

    this.element.style.setProperty("--survey-font", `"${font.family}", sans-serif`)
  }

  esc(str) {
    if (!str) return ""
    const el = document.createElement("span")
    el.textContent = str
    return el.innerHTML
  }
}
